/**
 * How a lifted set is written, on every surface that prints one: the Forma tables and dock, the
 * five-week matrix, the player's list, header and chips, and the attention queue's clip card.
 */

import { setDuration } from '~/shared/lib/clockDuration';
import { numberFormat } from '~/shared/lib/intlCache';
import type { Translate } from '~/shared/lib/localeTranslate';
import type { MeasurementFieldKey } from '~/shared/measurement';

import { lineWords, type LiftFields } from './liftLines';
import { setPosition, type LiftSession, type SetPosition, type SetRef } from './liftSessions';

export interface SetLoad {
  readonly weightKg: number | null;
  readonly reps: number | null;
}

/** A set with every figure a movement can measure; the ones a read predates are absent. */
export interface MeasuredSet extends SetLoad {
  readonly durationSeconds?: number | null;
  readonly distanceMeters?: number | null;
  readonly rounds?: number | null;
  readonly speedKmh?: number | null;
}

export interface SetWordsDeps {
  readonly t: Translate;
  readonly locale: string;
}

const kilograms = (locale: string) => numberFormat(locale, { maximumFractionDigits: 1 });

/**
 * A set with only one of its two figures names that figure with its unit; with neither it is
 * `null`, and the caller decides what an empty place looks like.
 */
function partial(set: SetLoad, { t, locale }: SetWordsDeps): string | null {
  if (set.reps !== null) return `${String(set.reps)} ${t('web:forma.unit.reps')}`;
  if (set.weightKg !== null) return `${kilograms(locale).format(set.weightKg)} ${t('web:forma.unit.weight')}`;
  return null;
}

/** "80 × 5" where a column already says what the figures are: a table cell or a chip. */
export function setCell(set: SetLoad, deps: SetWordsDeps): string | null {
  if (set.weightKg === null || set.reps === null) return partial(set, deps);
  return `${kilograms(deps.locale).format(set.weightKg)} × ${String(set.reps)}`;
}

/** "80 kg × 5" where the set stands on its own: the dock, the player's title and list. */
export function setHeadline(set: SetLoad, deps: SetWordsDeps): string | null {
  if (set.weightKg === null || set.reps === null) return partial(set, deps);
  return deps.t('web:forma.kgReps', {
    kg: kilograms(deps.locale).format(set.weightKg),
    reps: set.reps,
  });
}

/**
 * A set in its movement's own fields, where a column already says it is a set: „80 × 5", „12",
 * „45 s", „5,2 km · 26:10", „40 kg · 30 m". Only measured fields are written, load first, and a
 * movement whose fields are unknown is written as `setCell` writes it.
 */
export function setInFields(
  set: MeasuredSet,
  fields: LiftFields,
  deps: SetWordsDeps,
): string | null {
  if (fields === null) return setCell(set, deps);
  const { t } = deps;
  const measured = (field: MeasurementFieldKey, value: number | null | undefined): value is number =>
    fields.includes(field) && value != null && Number.isFinite(value);
  const parts: string[] = [];
  const reps = measured('reps', set.reps) ? set.reps : null;
  if (measured('weight', set.weightKg)) {
    const kg = kilograms(deps.locale).format(set.weightKg);
    parts.push(reps === null ? `${kg} ${t('web:forma.unit.weight')}` : `${kg} × ${String(reps)}`);
  }
  if (measured('rounds', set.rounds)) parts.push(`${String(set.rounds)} ${t('web:forma.unit.rounds')}`);
  if (reps !== null && !measured('weight', set.weightKg)) {
    parts.push(parts.length === 0 ? String(reps) : `${String(reps)} ${t('web:forma.unit.reps')}`);
  }
  if (measured('distance', set.distanceMeters)) {
    parts.push(lineWords('distance', deps, set.distanceMeters).withUnit(set.distanceMeters));
  }
  if (measured('duration', set.durationSeconds)) parts.push(setDuration(set.durationSeconds));
  if (measured('speed', set.speedKmh)) parts.push(lineWords('speed', deps).withUnit(set.speedKmh));
  return parts.length === 0 ? null : parts.join(' · ');
}

/** „Seria 3 z 4", „Seria 3", or „Z sesji" when the anchor names no set. */
export function setPositionLabel(position: SetPosition, t: Translate): string {
  switch (position.kind) {
    case 'counted':
      return t('web:forma.dock.setOf', { number: position.number, total: position.total });
    case 'numbered':
      return t('web:forma.dock.set', { number: position.number });
    case 'session':
      return t('web:forma.dock.inSession');
  }
}

export interface AnchorWords {
  /** „Seria 3 z 4", „Seria 3", or „Z sesji". */
  readonly place: string;
  /** „90 kg × 5", or `null` when the set carries neither figure. */
  readonly load: string | null;
}

/**
 * A filmed set in words, one helper for every surface that names it, so the dock, the gallery and
 * the player agree once the history is loaded. Without it the place is the logger's own number and
 * names no total.
 */
export function anchorWords(
  anchor: SetRef & SetLoad,
  sessions: readonly LiftSession[] | undefined,
  deps: SetWordsDeps,
): AnchorWords {
  return {
    place: setPositionLabel(setPosition(sessions ?? [], anchor), deps.t),
    load: setHeadline(anchor, deps),
  };
}

/**
 * A recording's set as a tile writes it after the day: „Seria 3 z 4", „90 kg × 5" — or „Poza sesją"
 * for a recording anchored to no session. The Forma gallery and the trainer's collection both.
 */
export function filmedSetWords(
  anchor: (SetRef & SetLoad) | null,
  sessions: readonly LiftSession[] | undefined,
  deps: SetWordsDeps,
): readonly string[] {
  if (anchor === null) return [deps.t('web:forma.dock.outside')];
  const set = anchorWords(anchor, sessions, deps);
  return set.load === null ? [set.place] : [set.place, set.load];
}
